import useInternalSearchUser from "@/internal/modules/users/composables/useInternalSearchUser";
import axios from "@/lib/axios";
import useUsersSearch from "@/modules/users/composables/useUsersSearch";
import { User } from "@/modules/users/interfaces/user";
import { useRoleUsersStore } from "@/stores/roleUsers";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { watch } from "vue";
import { ToastInterface, useToast } from "vue-toastification";

interface Params {
  roleId: number;
  company: number;
  immediate?: boolean;
}

const getRoleUsers = async (roleId: number): Promise<User[]> => {
  const { data } = await axios.get<User[]>(`api/roles/${roleId}/users`);

  return data;
};

const postRoleUsers = async (
  roleId: number,
  users: number[],
  toast: ToastInterface,
) => {
  try {
    const data = await axios.post(`api/roles/${roleId}/users`, {
      users,
    });

    toast.success("Usuarios asignados actualizados correctamente");
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const useRoleUsers = (props: Params) => {
  const { immediate = true } = props;
  const toast = useToast();

  const { roleId, roleUsers } = storeToRefs(useRoleUsersStore());
  const { setRoleUsers } = useRoleUsersStore();

  const { data, error, isLoading } = useQuery({
    queryKey: ["roleUsers", props.roleId],
    queryFn: () => getRoleUsers(props.roleId),
  });

  const roleUsersMutation = useMutation((users: number[]) =>
    postRoleUsers(props.roleId, users, toast),
  );

  const {
    data: users,
    isLoading: loadingUsers,
    setQuery,
    searchUsers,
  } = useUsersSearch({
    immediate: props.company ? true : false,
    automaticSearch: true,
    params: {
      filters: {
        company: props.company,
      },
    },
  });

  watch(
    data,
    (value) => {
      if (value) {
        setRoleUsers(value);
      }
    },
    {
      immediate,
    },
  );

  return {
    roleUsers,
    error,
    isLoading,

    users,
    loadingUsers,
    setQuery,
    searchUsers,

    roleUsersMutation,
    roleUsersMutationLoading: roleUsersMutation.isLoading,
    roleUsersMutationAsync: roleUsersMutation.mutateAsync,
  };
};

export default useRoleUsers;
